import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import { center } from '@turf/turf';
import {
  $activeExtrusionProp,
  $isExtrusion,
  $zoom10Hexagons,
} from '../../models/zoom10Model/index.js';
import {
  $hexagonsHeight,
  $interactivity,
  $invertHeight,
  $layersOpacity,
  $mapLoaded,
} from '../../models/mapModel/index.js';
import {
  $activeFilters,
  changeActiveFilterEv,
} from '../../models/activeFiltersModel/index.js';
import {
  changeLayerVisibility,
  changePointerOnLayer,
  flyTo,
} from '../../utils/mapbox-utils.js';
import { saveHexDataEv } from '../../models/indexCardsModel/index.js';
import { $gradientBuckets } from '../../models/gradientModel/index.js';
import {
  formDynamicBuckets,
  generateExtrusionHeight,
} from '../../utils/hexagon-utils.js';
import { delayedHideLoaderEv } from '../../models/webSocketModel/index.js';
import { $showPresets } from '../../models/dataPresetsModel/index.js';

function handleClick(e) {
  // if (window.map.getZoom() >= 13) {
  const feature = e.features[0];
  console.log('Feature click:: zoom10 hexagon', feature);
  changeActiveFilterEv({
    field: 'zoom10_hex',
    value: {
      id: feature.properties.hex_id,
      centerCoords: center(feature).geometry.coordinates,
    },
  });
  saveHexDataEv({
    level: 'zoom_10',
    data: {
      id: feature.properties.hex_id,
      indexes: feature.properties,
    },
  });
  // flyTo(center(feature).geometry.coordinates, 15);
  // }
}

function handleTouch(e) {
  if (e.originalEvent.touches && e.originalEvent.touches.length === 1) {
    handleClick(e);
  }
}

function Zoom10Layer() {
  const zoom10Hexagons = useUnit($zoom10Hexagons);
  const mapLoaded = useUnit($mapLoaded);
  const selectedZoom10Hexagon = useUnit($activeFilters).zoom10_hex;
  const selectedZoom9Hexagon = useUnit($activeFilters).zoom9_hex;
  const { buckets, colors } = useUnit($gradientBuckets);
  const interactivity = useUnit($interactivity);
  const layersOpacity = useUnit($layersOpacity);
  const isExtrusion = useUnit($isExtrusion);
  const activeExtrusionProp = useUnit($activeExtrusionProp);
  const hexagonsHeight = useUnit($hexagonsHeight);
  const showPresets = useUnit($showPresets);
  const invertHeight = useUnit($invertHeight);

  const createLayer = (update) => {
    const zoom10HexagonData = {
      type: 'FeatureCollection',
      features: zoom10Hexagons,
    };
    const gradientProp = formDynamicBuckets(buckets, colors);

    if (update) {
      window.map.getSource('zoom10_hexagon_source').setData(zoom10HexagonData);
      window.map.setPaintProperty(
        'zoom10_hexagon_layer',
        'fill-extrusion-color',
        gradientProp
      );
      window.map.setPaintProperty(
        'zoom10_hexagon_outline',
        'line-color',
        gradientProp
      );
    } else {
      if (!window.map.getSource('zoom10_hexagon_source')) {
        window.map.addSource('zoom10_hexagon_source', {
          type: 'geojson',
          data: zoom10HexagonData,
        });
      }

      if (!window.map.getLayer('zoom10_hexagon_layer')) {
        console.log('gradientProp: - ', gradientProp);
        window.map.addLayer({
          id: 'zoom10_hexagon_layer',
          // type: 'fill',
          source: 'zoom10_hexagon_source',
          type: 'fill-extrusion',
          paint: {
            'fill-extrusion-color': gradientProp,
            'fill-extrusion-height': generateExtrusionHeight(
              isExtrusion,
              activeExtrusionProp,
              hexagonsHeight,
              invertHeight
            ),
            'fill-extrusion-opacity': layersOpacity,
          },
        });
      }

      window.map.on('render', () => {
        delayedHideLoaderEv();
      });

      changePointerOnLayer('zoom10_hexagon_layer', true);

      // FIXME Border layer
      window.map.addLayer({
        id: 'zoom10_hexagon_outline',
        type: 'line',
        source: 'zoom10_hexagon_source',
        paint: {
          'line-color': gradientProp,
          'line-width': 1,
          'line-opacity': layersOpacity,
          // 'line-opacity': ['interpolate', ['linear'], ['zoom'], 9, 0, 10, 0.5],
        },
      });
      if (isExtrusion) {
        changeLayerVisibility('zoom10_hexagon_outline', 'none');
      } else {
        changeLayerVisibility('zoom10_hexagon_outline', 'visible');
      }
      if (showPresets) {
        changeLayerVisibility('zoom10_hexagon_layer', 'none');
        changeLayerVisibility('zoom10_hexagon_outline', 'none');
      } else {
        changeLayerVisibility('zoom10_hexagon_layer', 'visible');
        changeLayerVisibility(
          'zoom10_hexagon_outline',
          isExtrusion ? 'none' : 'visible'
        );
      }
    }
  };

  useEffect(() => {
    if (mapLoaded && zoom10Hexagons.length && buckets.length > 0) {
      if (window.map.getLayer('zoom10_hexagon_layer')) {
        createLayer(true);
      } else {
        setTimeout(() => {
          createLayer(false);
        }, 500);
      }
    }
  }, [zoom10Hexagons, mapLoaded, buckets]);

  useEffect(() => {
    if (mapLoaded && window.map.getLayer('zoom10_hexagon_layer')) {
      if (selectedZoom10Hexagon.id !== '') {
        flyTo(selectedZoom10Hexagon.centerCoords, 15);
      } else if (selectedZoom9Hexagon.centerCoords.length > 0) {
        flyTo(selectedZoom9Hexagon.centerCoords, 13);
      }
    }
  }, [selectedZoom10Hexagon.id]);

  useEffect(() => {
    if (interactivity) {
      window.map.on('click', 'zoom10_hexagon_layer', handleClick);
      window.map.on('touchstart', 'zoom10_hexagon_layer', handleTouch);
      changePointerOnLayer('zoom10_hexagon_layer', interactivity);
    } else {
      window.map.off('click', 'zoom10_hexagon_layer', handleClick);
      window.map.off('touchstart', 'zoom10_hexagon_layer', handleTouch);
      changePointerOnLayer('zoom10_hexagon_layer', interactivity);
    }
  }, [interactivity]);

  useEffect(() => {
    if (
      mapLoaded &&
      window.map.getLayer('zoom10_hexagon_layer') &&
      buckets.length > 0
    ) {
      createLayer(true);
    }
  }, [buckets]);

  useEffect(() => {
    if (mapLoaded && window.map.getLayer('zoom10_hexagon_layer')) {
      setTimeout(() => {
        window.map.setPaintProperty(
          'zoom10_hexagon_layer',
          'fill-extrusion-opacity',
          layersOpacity
        );
        window.map.setPaintProperty(
          'zoom10_hexagon_outline',
          'line-opacity',
          layersOpacity
        );
      }, 100);
    }
  }, [mapLoaded, layersOpacity]);

  useEffect(() => {
    if (mapLoaded && window.map.getLayer('zoom10_hexagon_layer')) {
      if (!isExtrusion) {
        window.map.setPaintProperty(
          'zoom10_hexagon_layer',
          'fill-extrusion-height',
          generateExtrusionHeight(isExtrusion, '', 0, invertHeight)
        );
      } else {
        window.map.setPaintProperty(
          'zoom10_hexagon_layer',
          'fill-extrusion-height',
          generateExtrusionHeight(
            isExtrusion,
            activeExtrusionProp,
            hexagonsHeight,
            invertHeight
          )
        );
      }
    }
  }, [
    isExtrusion,
    activeExtrusionProp,
    hexagonsHeight,
    showPresets,
    invertHeight,
  ]);

  useEffect(() => {
    if (mapLoaded && window.map.getLayer('zoom10_hexagon_layer')) {
      if (showPresets) {
        changeLayerVisibility('zoom10_hexagon_layer', 'none');
        changeLayerVisibility('zoom10_hexagon_outline', 'none');
      } else {
        changeLayerVisibility('zoom10_hexagon_layer', 'visible');
        changeLayerVisibility(
          'zoom10_hexagon_outline',
          isExtrusion ? 'none' : 'visible'
        );
      }
    }
  }, [showPresets, isExtrusion]);

  return null;
}

export default Zoom10Layer;
